<template>
  <div class="payment-step-container">
    <a-alert
      show-icon
      type="info"
      class="invoice-alert"
    >
      <a-icon slot="icon" type="file-text" />
      <div slot="description" class="invoice-note-wrapper">
        <span v-html="$t('PAYMENT_INVOICE_NOTE')" />
        <a
          href="javascript:void(0)"
          class="invoice-zalo-link"
          @click="openInvoiceModal"
        >
          {{ $t('PAYMENT_INVOICE_CONTACT_ZALO') }}
        </a>
      </div>
    </a-alert>

    <a-card class="payment-card card">
      <div slot="title" class="card-title">
        {{ $t('PAYMENT_PROCCESS_TITLE') }}
      </div>

      <a-row :gutter="24" type="flex" class="payment-body">
        <a-col
          v-if="paymentQrCode"
          :xs="24"
          :md="11"
          class="qr-col"
        >
          <div class="qr-box">
            <a-popover
              trigger="hover"
              placement="right"
              overlay-class-name="qr-zoom-popover"
            >
              <img
                slot="content"
                :src="paymentQrCode"
                style="display: block; width: 460px; max-width: 90vw; height: auto;"
              >
              <img
                :src="paymentQrCode"
                class="qr-code-image hand-mouse"
              >
            </a-popover>
          </div>
        </a-col>

        <a-col
          :xs="24"
          :md="paymentQrCode ? 13 : 24"
          class="detail-col"
        >
          <div class="detail-row">
            <span class="detail-label">{{ $t('PAYMENT_TRANSFER_TO') }}</span>
            <div class="detail-value method-value">
              <img
                v-if="payment.method && payment.method.logo"
                :src="payment.method.logo"
                class="payment-method-logo"
              >
              <div v-html="paymentMethodText" class="payment-method-text" />
            </div>
          </div>

          <div class="detail-row">
            <span class="detail-label">{{ $t('PAYMENT_PROCCESS_VALUE_BY_MONEY') }}</span>
            <div class="detail-value">
              <span class="payment-amount">{{ paymentTotal }}</span>
            </div>
          </div>

          <div class="detail-row">
            <span class="detail-label">{{ $t('PAYMENT_PROCCESS_VALUE_BY_NOTE') }}</span>
            <div class="detail-value note-value">
              <span class="payment-note-text">{{ paymentNote }}</span>
            </div>
          </div>

          <a-button
            block
            icon="copy"
            class="copy-all-btn"
            @click="handleCopy(transferInfoText)"
          >
            {{ $t('PAYMENT_COPY_ALL') }}
          </a-button>
        </a-col>
      </a-row>

      <a-alert
        show-icon
        type="success"
        class="success-alert"
      >
        <div slot="description">
          <span v-html="paymentPreNote" />
        </div>
      </a-alert>

      <a-button
        type="primary"
        class="confirm-payment-btn"
        @click="handleConfirmPayment"
      >
        {{ $t("CONFIRM_PAYMENT_STEP") }}
      </a-button>
    </a-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'PaymentStep',
  props: {
    paymentQrCode: {
      type: String,
      default: '',
    },
    payment: {
      type: Object,
      required: true,
    },
    paymentMethodText: {
      type: String,
      default: '',
    },
    paymentMethodId: {
      type: [String, Number],
      default: '',
    },
    paymentTotal: {
      type: String,
      required: true,
    },
    paymentNote: {
      type: String,
      required: true,
    },
    paymentPreNote: {
      type: String,
      required: true,
    },
  },
  computed: {
    transferInfoText() {
      const method = this.payment && this.payment.method ? this.payment.method : {};
      const bankName = method.code || method.name || '';
      return [
        `${this.$t('PAYMENT_COPY_LABEL_ACCOUNT')}: ${this.paymentMethodId}`,
        `${this.$t('PAYMENT_COPY_LABEL_BANK')}: ${bankName}`,
        `${this.$t('PAYMENT_COPY_LABEL_OWNER')}: ${method.owner || ''}`,
        `${this.$t('PAYMENT_COPY_LABEL_AMOUNT')}: ${this.paymentTotal}`,
        `${this.$t('PAYMENT_COPY_LABEL_NOTE')}: ${this.paymentNote}`,
      ].join('\n');
    },
  },
  methods: {
    ...mapActions('ui', ['openInvoiceModal']),
    handleCopy(content) {
      this.$emit('copy', content);
    },
    handleConfirmPayment() {
      this.$emit('confirm-payment');
    },
  },
};
</script>

<style scoped>
.payment-step-container {
  margin-top: var(--spacing-md);
}

.payment-card {
  margin-top: var(--spacing-md);
}

.payment-body {
  align-items: stretch;
}

/* QR column */
.qr-col {
  text-align: center;
}

.qr-box {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
}

.qr-code-image {
  max-height: 300px;
  width: auto;
  max-width: 100%;
  border-radius: var(--border-radius-md);
}

@media (max-width: 768px) {
  .qr-col {
    margin-bottom: var(--spacing-md);
  }

  .qr-code-image {
    max-height: 240px;
    max-width: 240px;
  }
}

/* Detail rows */
.detail-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-sm);
}

.detail-row {
  padding: var(--spacing-sm) 0;
  border-bottom: 1px dashed var(--border-color);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 4px;
}

.detail-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.method-value .payment-method-logo {
  height: 36px;
  width: auto;
  max-width: 90px;
  object-fit: contain;
  flex-shrink: 0;
}

.payment-method-text {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-relaxed);
  flex: 1;
  word-break: break-word;
}

.payment-note-text {
  flex: 1;
  color: var(--primary-color);
  font-weight: var(--font-weight-bold);
  word-break: break-word;
}

.copy-all-btn {
  margin-top: var(--spacing-md);
  height: 44px;
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-md);
  touch-action: manipulation;
}

.payment-amount {
  font-size: 1.625rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.02em;
  color: var(--primary-color);
}

@media (max-width: 768px) {
  .payment-amount {
    font-size: var(--font-size-xl);
  }
}

.confirm-payment-btn {
  margin-top: var(--spacing-md);
  min-width: 220px;
  height: 48px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  border-radius: var(--border-radius-md);
  touch-action: manipulation;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 768px) {
  .confirm-payment-btn {
    min-width: 100%;
    height: 48px;
  }
}

.success-alert {
  width: 100%;
  margin-top: var(--spacing-md);
  border-radius: var(--border-radius-md);
}

.invoice-alert {
  width: 100%;
  border-radius: var(--border-radius-md);
  margin-bottom: var(--spacing-md);
}

.invoice-note-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.invoice-zalo-link {
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  white-space: nowrap;
}

.hand-mouse {
  cursor: pointer;
}
</style>
